import React from 'react';
import { CtaButton } from '../../../../types';
import { ImageMetadata } from '../../../../services/imageService';
import { CtaButtonList, CtaButtonEditDialog } from './popupCta';

interface PopupCTATabProps {
  editState?: any;
  siteId?: string;
  onUpdateCtaButton: (ctaButtonData: { html: string; css: string; image?: string; hoverImage?: string; enabled: boolean; bottomPercent?: number; widthPercent?: number }) => void;
  onUpdateCtaButtons?: (ctaButtons: CtaButton[]) => void;
  cachedImages?: ImageMetadata[];
  preloaded?: boolean;
  onPendingCtaImageFile?: (buttonId: string, file: File | null) => void;
  onPendingCtaHoverImageFile?: (buttonId: string, file: File | null) => void;
}

export const PopupCTATab: React.FC<PopupCTATabProps> = ({ 
  editState, 
  siteId, 
  onUpdateCtaButton,
  onUpdateCtaButtons,
  cachedImages = [],
  preloaded = false,
  onPendingCtaImageFile,
  onPendingCtaHoverImageFile,
}) => {
  const [ctaButtons, setCtaButtons] = React.useState<CtaButton[]>([]);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [editingButton, setEditingButton] = React.useState<CtaButton | null>(null);
  const [initialized, setInitialized] = React.useState(false);

  // 初期化：既存データの読み込みと移行
  React.useEffect(() => {
    if (editState && !initialized) {
      // 新しいデータ構造（ctaButtons）が存在する場合
      if (editState.settings?.ctaButtons && Array.isArray(editState.settings.ctaButtons)) {
        setCtaButtons(editState.settings.ctaButtons);
      } 
      // 旧データ構造（ctaButton）からの移行
      else if (editState.settings?.ctaButton) {
        const oldButton = editState.settings.ctaButton;
        const migratedButton: CtaButton = {
          id: 'cta-migrated-1',
          name: 'デフォルトCTAボタン',
          html: oldButton.html || '',
          css: oldButton.css || '',
          image: oldButton.image,
          hoverImage: oldButton.hoverImage,
          bottomPercent: oldButton.bottomPercent,
          widthPercent: oldButton.widthPercent ?? 80
        };
        setCtaButtons([migratedButton]);
        
        // 新しい構造で保存
        if (onUpdateCtaButtons) {
          onUpdateCtaButtons([migratedButton]);
        }
      }
      setInitialized(true);
    }
  }, [editState, initialized, onUpdateCtaButtons]);

  // CTAボタンが変更されたら親に通知
  React.useEffect(() => {
    if (initialized && onUpdateCtaButtons) {
      onUpdateCtaButtons(ctaButtons);
    }
  }, [ctaButtons, initialized, onUpdateCtaButtons]);

  const handleAdd = () => {
    setEditingButton({
      id: `cta-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      name: '',
      html: '',
      css: '',
    });
    setEditDialogOpen(true);
  };

  const handleEdit = (ctaButton: CtaButton) => {
    setEditingButton(ctaButton);
    setEditDialogOpen(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('このCTAボタンを削除してもよろしいですか？')) {
      onPendingCtaImageFile?.(id, null);
      onPendingCtaHoverImageFile?.(id, null);
      setCtaButtons(prev => prev.filter(btn => btn.id !== id));
    }
  };

  const handleSave = (ctaButton: CtaButton) => {
    setCtaButtons(prev => {
      const exists = prev.find(btn => btn.id === ctaButton.id);
      if (exists) {
        // 更新
        return prev.map(btn => btn.id === ctaButton.id ? ctaButton : btn);
      } else {
        // 新規追加
        return [...prev, ctaButton];
      }
    });
  };

  return (
    <>
      <CtaButtonList
        ctaButtons={ctaButtons}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onAdd={handleAdd}
      />

      <CtaButtonEditDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setEditingButton(null);
        }}
        onSave={handleSave}
        onDelete={handleDelete}
        ctaButton={editingButton}
        siteId={siteId}
        cachedImages={cachedImages}
        preloaded={preloaded}
        editState={editState}
        onPendingCtaImageFile={onPendingCtaImageFile}
        onPendingCtaHoverImageFile={onPendingCtaHoverImageFile}
      />
    </>
  );
};
